import { useRef } from 'react';
import {
  BirthdayPicker,
  IconChevronDown,
  IconValidationInvalid,
  InputPanel,
  KeyboardlessInput,
  type KeyboardlessInputProps,
} from '@adc/parallax-component-library';
import { DateTimeFormatter, LocalDate } from '@js-joda/core';
import i18n from 'Utilities/i18n';

type BirthdayInputProps = Omit<
  KeyboardlessInputProps,
  'value' | 'label' | 'aria-label' | 'placeholder' | 'children'
> & {
  initialDate?: LocalDate;
  startDate?: LocalDate;
  endDate?: LocalDate;
  isTouched?: boolean;
  errorMessage?: string;
  onDateChange: (date: LocalDate, cancelled: boolean) => void;
};

// initial date is used as date for contents for the input field
const BirthdayInput = ({
  initialDate,
  startDate = LocalDate.now().minusYears(100),
  endDate = LocalDate.now(),
  isTouched,
  errorMessage,
  error,
  testID,
  onDateChange,
  ...props
}: BirthdayInputProps) => {
  const currentDateRef = useRef<LocalDate>(initialDate ?? LocalDate.now().minusYears(18));

  const handleSelectDate = (date: LocalDate) => {
    currentDateRef.current = date;
  };

  const hasError = isTouched && error;

  return (
    <KeyboardlessInput
      placeholder={i18n.t(
        'AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.dob.placeholder'
      )}
      label={i18n.t(
        'AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.dob.label'
      )}
      aria-label={i18n.t(
        'AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.dob.label'
      )}
      value={
        isTouched
          ? initialDate
            ? initialDate.format(DateTimeFormatter.ofPattern('MM/dd/yyyy'))
            : undefined
          : undefined
      }
      error={hasError}
      supportingIconConfig={hasError ? { icon: <IconValidationInvalid /> } : undefined}
      supportingText={hasError ? errorMessage : undefined}
      inlineIconConfig={{
        icon: <IconChevronDown />,
        'aria-label': 'icon',
        color: '$interactive.core.enabled',
        disabledColor: '$interactive.core.disabled',
      }}
      testID={testID}
      {...props}
    >
      {({ blurInput, hidePanel, inputPanelRef }) => (
        <InputPanel
          ref={inputPanelRef}
          onDismiss={blurInput}
          testID="date-picker"
          topNavigationProps={{
            title: i18n.t(
              'AccountInformationPersonalInfo.content.accountInformationPersonalInfoForm.formField.dob.label'
            ),
            buttonStart: {
              element: i18n.t('Global.microcopy.common.cancel'),
              onPress: () => {
                hidePanel();
                // the below ref remembers dates changing, so reset to initial or current date
                currentDateRef.current = initialDate ?? LocalDate.now().minusYears(18);
                onDateChange(currentDateRef.current, true);
              },
            },
            buttonEnd: {
              element: i18n.t('Global.microcopy.common.save'),
              onPress: () => {
                hidePanel();
                onDateChange(currentDateRef.current, false);
              },
            },
          }}
        >
          <BirthdayPicker
            initialDate={currentDateRef.current}
            startDate={startDate}
            endDate={endDate}
            onDateChange={handleSelectDate}
          />
        </InputPanel>
      )}
    </KeyboardlessInput>
  );
};

export { BirthdayInput };
